import { useCallback, useEffect, useRef, useState } from "react";
import { DynamicFieldItem } from "../../../../../model/dynamic-forms";

export function useDynamicFormState(
	preselectedFormValues: DynamicFieldItem[] | undefined,
	onChange?: (values: DynamicFieldItem[]) => void
) {
	const [values, setValues] = useState<DynamicFieldItem[]>(preselectedFormValues ?? []);

	// Keep a stable ref to onChange so set/remove callbacks don't need to depend on it.
	const onChangeRef = useRef(onChange);
	useEffect(() => {
		onChangeRef.current = onChange;
	}, [onChange]);

	const setField = useCallback((item: DynamicFieldItem) => {
		setValues(prev => {
			const index = prev.findIndex(v => v.fieldId === item.fieldId);
			const next = index >= 0 ? prev.map((v, i) => (i === index ? item : v)) : [...prev, item];
			onChangeRef.current?.(next);
			return next;
		});
	}, []);

	const removeField = useCallback((fieldId: string) => {
		setValues(prev => {
			if (!prev.some(v => v.fieldId === fieldId)) return prev;
			const next = prev.filter(v => v.fieldId !== fieldId);
			onChangeRef.current?.(next);
			return next;
		});
	}, []);

	const getField = useCallback((fieldId: string) => values.find(v => v.fieldId === fieldId), [values]);

	return { values, setField, removeField, getField };
}
